import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiHelpers } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import type { TopicCluster, TopicMapResponse, TopicNode } from '@/types';
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  Sparkles,
  Map as MapIcon,
  CheckCircle2,
  Clock,
  FileEdit,
  AlertTriangle,
  X,
  RotateCcw,
  BarChart3,
} from 'lucide-react';

/**
 * Topic Map — the blog's living plan: clusters of pillar + spoke topics the
 * topic engine instantiated from this business's profile and catalog, with
 * the engine's scores, Search Console numbers for written posts, rewrite
 * recommendations, and overlaps in the back catalog.
 *
 * Read-mostly. The engine runs weekly on its own; "Run now" forces a run
 * (up to 5 suggestions) and "Rebuild map" regenerates the clusters.
 */
export default function BlogTopicMap() {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const [busyNode, setBusyNode] = useState<string | null>(null);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['blog-topic-map', projectId],
    queryFn: () => apiHelpers.getBlogTopicMap(projectId!),
    enabled: Boolean(projectId),
  });
  const payload: TopicMapResponse | undefined = data?.data?.data;
  const map = payload?.map;
  const readiness = payload?.readiness;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['blog-topic-map', projectId] });
    queryClient.invalidateQueries({ queryKey: ['blog-suggestions', projectId] });
  };

  const run = useMutation({
    mutationFn: (regenerate: boolean) => apiHelpers.runBlogTopicEngine(projectId!, regenerate),
    onSuccess: (res) => {
      const d = res.data?.data;
      toast.success(
        d?.created
          ? `Queued ${d.created} topic${d.created === 1 ? '' : 's'} — ${d.summary}`
          : `Engine ran — ${d?.summary || 'nothing new to queue'}`
      );
      invalidate();
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'The topic engine could not run.')),
  });

  const metrics = useMutation({
    mutationFn: () => apiHelpers.refreshBlogTopicMetrics(projectId!),
    onSuccess: (res) => {
      const d = res.data?.data;
      toast.success(
        d?.skipped
          ? `Metrics skipped: ${d.skipped}`
          : `Refreshed Search Console data for ${d?.posts ?? 0} posts (${d?.rewrites ?? 0} rewrite recommendations)`
      );
      invalidate();
    },
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not refresh metrics.')),
  });

  const setStatus = useMutation({
    mutationFn: ({ nodeId, status }: { nodeId: string; status: 'planned' | 'dismissed' }) =>
      apiHelpers.updateBlogTopicNode(projectId!, nodeId, status),
    onMutate: (v) => setBusyNode(v.nodeId),
    onSuccess: invalidate,
    onError: (err: unknown) => toast.error(apiErrorMessage(err, 'Could not update the topic.')),
    onSettled: () => setBusyNode(null),
  });

  const counts = summarize(map?.clusters || []);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Link
          to={`/projects/${projectId}/blog`}
          className="hover:bg-muted rounded-lg p-2 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <MapIcon className="h-6 w-6" /> Topic Map
          </h1>
          <p className="text-muted-foreground text-sm">
            Clusters of winnable topics built from your business profile and catalog. The engine
            queues the highest-scoring ones into Trend Suggestions every week; the auto-blog writes
            them first.
          </p>
        </div>
        <button
          onClick={() => metrics.mutate()}
          disabled={metrics.isPending}
          className="hover:bg-muted inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
        >
          {metrics.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <BarChart3 className="h-4 w-4" />
          )}
          Refresh metrics
        </button>
        <button
          onClick={() => run.mutate(true)}
          disabled={run.isPending}
          className="hover:bg-muted inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:opacity-60"
        >
          <RefreshCw className="h-4 w-4" /> Rebuild map
        </button>
        <button
          onClick={() => run.mutate(false)}
          disabled={run.isPending || readiness?.ok === false}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {run.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Run now
        </button>
      </div>

      {readiness && !readiness.ok && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-400/60 bg-amber-50 p-4 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-medium">The engine will not run yet</div>
            <div>{readiness.reason}</div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !map || map.clusters.length === 0 ? (
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground">
            No topic map yet. {readiness?.ok ? 'Click "Run now" to build it.' : ''}
          </p>
          {map?.lastRunSummary && (
            <p className="text-muted-foreground mt-2 text-xs">{map.lastRunSummary}</p>
          )}
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-5">
            <Stat label="Clusters" value={map.clusters.length} />
            <Stat label="Planned" value={counts.planned} icon={<Clock className="h-4 w-4" />} />
            <Stat label="Queued" value={counts.queued} icon={<Sparkles className="h-4 w-4" />} />
            <Stat
              label="Written"
              value={counts.written}
              icon={<CheckCircle2 className="h-4 w-4" />}
            />
            <Stat
              label="Rewrite"
              value={counts.rewrite}
              icon={<FileEdit className="h-4 w-4" />}
              tone={counts.rewrite ? 'warn' : undefined}
            />
          </div>
          <div className="text-muted-foreground text-xs">
            Business type <span className="font-medium">{map.businessType}</span> · map v
            {map.version} generated {new Date(map.generatedAt).toLocaleDateString()}
            {map.lastRunAt
              ? ` · last run ${new Date(map.lastRunAt).toLocaleString()} — ${map.lastRunSummary || ''}`
              : ' · not run yet'}
            {isFetching ? ' · refreshing…' : ''}
          </div>

          {map.overlaps.length > 0 && (
            <div className="rounded-lg border border-amber-400/40 p-4 text-sm">
              <div className="mb-2 flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Overlapping posts (
                {map.overlaps.length})
              </div>
              <ul className="space-y-1">
                {map.overlaps.slice(0, 8).map((o, i) => (
                  <li key={i} className="text-muted-foreground">
                    <span className="text-foreground">{o.titles.join('  ↔  ')}</span> — {o.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-6">
            {map.clusters.map((c) => (
              <ClusterCard
                key={c.id}
                cluster={c}
                busyNode={busyNode}
                onStatus={(nodeId, status) => setStatus.mutate({ nodeId, status })}
                projectId={projectId!}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function summarize(clusters: TopicCluster[]) {
  const out = { planned: 0, queued: 0, written: 0, rewrite: 0 };
  for (const c of clusters) {
    for (const s of c.spokes) if (s.status in out) out[s.status as keyof typeof out]++;
  }
  return out;
}

function Stat({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon?: React.ReactNode;
  tone?: 'warn';
}) {
  return (
    <div className={`rounded-lg border p-3 ${tone === 'warn' ? 'border-amber-400/60' : ''}`}>
      <div className="text-muted-foreground flex items-center gap-1 text-xs uppercase">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

const STATUS_STYLE: Record<string, string> = {
  planned: 'bg-muted text-muted-foreground',
  queued: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  written: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rewrite: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  dismissed: 'bg-muted text-muted-foreground line-through',
};

function ClusterCard({
  cluster,
  busyNode,
  onStatus,
  projectId,
}: {
  cluster: TopicCluster;
  busyNode: string | null;
  onStatus: (nodeId: string, status: 'planned' | 'dismissed') => void;
  projectId: string;
}) {
  const written = cluster.spokes.filter(
    (s) => s.status === 'written' || s.status === 'rewrite'
  ).length;
  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
        <div>
          <h2 className="text-lg font-semibold">{cluster.name}</h2>
          <div className="text-muted-foreground text-xs">
            Pillar: <span className="text-foreground">{cluster.pillar.title}</span> · {written}/
            {cluster.spokes.length} spokes written
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {cluster.terms.slice(0, 8).map((t) => (
            <span key={t} className="bg-muted rounded px-2 py-0.5 text-[11px]">
              {t}
            </span>
          ))}
        </div>
      </div>
      <ul className="divide-y">
        {cluster.spokes.map((s) => (
          <NodeRow
            key={s.id}
            node={s}
            busy={busyNode === s.id}
            onStatus={onStatus}
            projectId={projectId}
          />
        ))}
      </ul>
    </div>
  );
}

function NodeRow({
  node,
  busy,
  onStatus,
  projectId,
}: {
  node: TopicNode;
  busy: boolean;
  onStatus: (nodeId: string, status: 'planned' | 'dismissed') => void;
  projectId: string;
}) {
  const s = node.scores;
  return (
    <li className="flex flex-wrap items-start gap-3 p-4 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[node.status] || ''}`}
          >
            {node.status}
          </span>
          <span className="bg-muted rounded px-2 py-0.5 text-[11px]">
            {node.archetype || 'pillar'}
          </span>
          <span className="font-medium">
            {node.postId ? (
              <Link
                to={`/projects/${projectId}/blog/${node.postId}/edit`}
                className="hover:underline"
              >
                {node.title}
              </Link>
            ) : (
              node.title
            )}
          </span>
        </div>
        <div className="text-muted-foreground mt-1 text-xs">
          <span className="font-mono">{node.targetQuery}</span>
          {node.productToFeature ? ` · features ${node.productToFeature}` : ''}
        </div>
        {node.rationale && (
          <div className="text-muted-foreground mt-1 text-xs">{node.rationale}</div>
        )}
        {s && (
          <div className="text-muted-foreground mt-1 text-[11px]">
            score {s.priority.toFixed(2)} · fit {s.fit.toFixed(2)} · winnability{' '}
            {s.winnability.toFixed(2)} · demand {s.demand.toFixed(2)} · gap {s.gap.toFixed(2)}
            {s.serpNote ? ` · ${s.serpNote}` : ''}
          </div>
        )}
        {node.metrics && (
          <div className="mt-1 text-[11px]">
            <span className="text-muted-foreground">Search Console ({node.metrics.weekKey}):</span>{' '}
            {node.metrics.impressions} impr · {node.metrics.clicks} clicks
            {node.metrics.position ? ` · pos ${node.metrics.position}` : ''}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {node.status === 'dismissed' ? (
          <button
            onClick={() => onStatus(node.id, 'planned')}
            disabled={busy}
            title="Restore"
            className="hover:bg-muted rounded p-1.5 transition-colors disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RotateCcw className="h-4 w-4" />
            )}
          </button>
        ) : node.status === 'planned' ? (
          <button
            onClick={() => onStatus(node.id, 'dismissed')}
            disabled={busy}
            title="Dismiss — never suggest this"
            className="hover:bg-muted rounded p-1.5 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
          </button>
        ) : null}
      </div>
    </li>
  );
}
